import { type CONVEYANCE_TYPES } from '@koala/sdk';
import { getMenu, isUpstreamRequestError } from '@koala/sdk/v4';
import Router from 'next/router';
import { call, put, select, takeLatest, type SagaReturnType } from 'redux-saga/effects';
import basketActions from '../basket/actions';
import conveyanceModeActions from '../conveyanceMode/actions';
import actions from './actions';
import { ERROR_MESSAGES } from '@/constants/events';
import { DELIVERY_TIME_WANTED_MODES } from '@/constants/global';
import { createHttpClient } from '@/services/client';
import { type RootState } from '@/types/app';
import { getOrigin } from '@/utils';
import { isWantedAtValidationError } from '@/utils/errors';
import { prepareErrorMessage } from '@/utils/global';
import * as ErrorReporter from '@/utils/errorReporter';
import { getMenuFromStorage } from '@/utils/menu';
import { isNonJsonResponseError, isTransportFailure } from '@/utils/networkErrors';
import { retryGuardedReadSaga } from '@/utils/withJsonGuard';

export function* refreshBasketMenuSaga(action: ReturnType<typeof actions.refreshBasketMenu>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    let wantedAt: string | undefined = undefined;

    if (action.wantedAt && action.wantedAt !== DELIVERY_TIME_WANTED_MODES.ASAP) {
      wantedAt = action.wantedAt;
    }

    const [response, compressedMenu]: SagaReturnType<typeof getMenu> = yield* retryGuardedReadSaga(
      () =>
        getMenu(
          {
            id: action.id,
            ifModifiedSince: undefined,
            compressed: true,
            wantedAt,
          },
          { client },
        ),
      { route: Router.pathname, urlHint: '/menus', locationId: action.id?.toString() },
    );

    // @ts-expect-error
    yield put(actions.setBasketMenu(response, compressedMenu));

    // Ensure that the cart doesn't contain any items no longer in the menu
    yield put(basketActions.revalidateBasketItems(response));
  } catch (error) {
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      `Failed to fetch menu for the following location: ${action.id}.`,
      error,
    );

    /**
     * TEAM-14594: coo-api rejects a stored pickup time that falls outside the location's
     * ordering horizon (`wanted-at` must be before end-of-day + order_ahead_days). Sending
     * it again cannot help — the same time comes back — so this is not a retry: drop the
     * stored time so the menu is read for now instead.
     *
     * Leaving it in place is what stranded the diner. The time outlives the request in both
     * the conveyance slice and the persisted basket, so every later read re-sent it and the
     * store page's "Try again" re-issued the identical failing call.
     *
     * `setWantedTime` clears both stores and its own saga re-requests the menu without a
     * wanted-at, so no refetch is dispatched here — a second one would only duplicate it.
     * Requiring `action.wantedAt` also bounds this to one attempt: the retry carries no
     * time, and a request without one cannot fail this validation.
     */
    if (action.wantedAt && isWantedAtValidationError(error, errorResponse)) {
      // Reported before dispatching: `takeLatest` cancels this task once the refetch
      // triggered downstream is taken, and the event would be lost with it.
      // Not at 'error' level — the diner is recovered, not blocked — but still visible, so
      // the volume reaching this path stays measurable while the upstream causes are open.
      ErrorReporter.captureException(error, 'info', {
        eventName: ERROR_MESSAGES.FETCH_BASKET_MENU_ERROR,
        errorResponse,
        recovery: 'cleared the rejected wanted-at; menu re-requested without it',
      });

      const conveyanceType: CONVEYANCE_TYPES = yield select(
        (state: RootState) => state.app.conveyanceMode.type,
      );

      yield put(conveyanceModeActions.setWantedTime(undefined, action.id, conveyanceType));

      // TEAM-14373 (AC4): also clear the stale time from the three checkout-specific
      // stores that `initializeSessionStorageBasket` reads (in priority order):
      //   1. checkoutBasket.wanted_at
      //   2. fulfillment.address.time_wanted
      //   3. fulfillment.time_wanted
      // Without this, "Edit Order" → re-checkout re-sends the same out-of-range value
      // even though the conveyance time was cleared above.
      yield put(basketActions.clearCheckoutWantedAt());
      const state = (yield select()) as RootState;
      const fulfillment = state.app.basket.fulfillment;
      if (fulfillment) {
        yield put(
          basketActions.updateBasketFulfillment({
            ...fulfillment,
            time_wanted: undefined,
            address: fulfillment.address
              ? { ...fulfillment.address, time_wanted: null }
              : fulfillment.address,
          }),
        );
      }

      return;
    }

    yield put({ type: actions.FETCH_BASKET_MENU_ERROR });

    // `retryGuardedReadSaga` already reported this failure once on final failure (a 5xx / HTML as an
    // `error`, a dropped connection as a warning-level `network_failure`). Re-capturing it here would
    // report the same failure twice — the double-report this ticket removes (TEAM-15165). Still
    // report anything the guard does not: a 4xx, a thrown guard assertion, or an unexpected error.
    const reportedByGuard =
      isNonJsonResponseError(error) || isUpstreamRequestError(error) || isTransportFailure(error);
    if (!reportedByGuard) {
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.FETCH_BASKET_MENU_ERROR,
        errorResponse,
      });
    }
  }
}

function* hydrateBasketMenuFromStorageSaga() {
  try {
    const menuFromStorage = getMenuFromStorage();
    yield put(actions.setBasketMenuFromStorage(menuFromStorage));
  } catch (error) {
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'Failed to get menu from storage.',
      error,
    );
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
    });
  }
}

export default function* rootSaga() {
  yield takeLatest(actions.HYDRATE_BASKET_MENU, hydrateBasketMenuFromStorageSaga);

  yield takeLatest(actions.FETCH_BASKET_MENU, refreshBasketMenuSaga);
}
